import gql from 'graphql-tag';
import {Base64} from 'js-base64';

import {ChemicalStructureBlob} from 'chemistry/ChemistryConstants';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ExternalFile} from 'notebook/util/NoteUtilConstants';
import {textToBlob, initiateFileDownload, initiateTextDownload} from 'util/TextToBlobUtil';

import dataURIToBlob from '../images/dataURIToBlob';
import {
  DownloadChemicalFileQuery,
  DownloadChemicalFileQueryVariables,
} from './__generated/DownloadChemicalFileQuery';

export const DOWNLOAD_CHEMICAL_FILE_QUERY = gql`
  query DownloadChemicalFileQuery(
    $apiId: StrictString!
    $url: StrictString!
    $owningEntityApiId: StrictString
    $versionId: StrictInt
  ) @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    signedFile(
      apiId: $apiId
      url: $url
      addHtmlContentTypeHeader: false
      owningEntityApiId: $owningEntityApiId
      versionId: $versionId
    ) {
      ... on SignedFileNode {
        signedUrl
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const stubbableFunctions = {
  async chemicalFileSignedURL(externalFile: ExternalFile): Promise<string> {
    const externalFileURL = await doQueryWithoutCache<
      DownloadChemicalFileQuery,
      DownloadChemicalFileQueryVariables
    >(
      {query: DOWNLOAD_CHEMICAL_FILE_QUERY},
      {variables: {apiId: externalFile.id, url: externalFile.url, owningEntityApiId: '', versionId: null}}
    );
    if (isInaccessible(externalFileURL.signedFile)) {
      return '';
    }
    const {signedUrl} = externalFileURL.signedFile;
    return signedUrl;
  },
};

export async function chemicalStructureFromFile(
  externalFile: ExternalFile,
  fileType: MarvinChemicalFormat
): Promise<string> {
  const externalFileURL = await stubbableFunctions.chemicalFileSignedURL(externalFile);
  let initialSketcherValue: string;
  try {
    const response = await fetch(externalFileURL);
    if (fileType === MarvinChemicalFormat.CDX) {
      const initialChemicalBuffer = await response.arrayBuffer();
      const initialChemicalIntArray = new Uint8Array(initialChemicalBuffer);
      initialSketcherValue = Base64.fromUint8Array(initialChemicalIntArray);
    } else {
      initialSketcherValue = await response.text();
    }
  } catch (err) {
    throw new Error('Unable to open structure for editing.');
  }
  return initialSketcherValue;
}

export function createChemicalStructureBlob(
  structure: string,
  basename: string,
  saveFormat: MarvinChemicalFormat
): ChemicalStructureBlob {
  const filename = `${basename}.${saveFormat}`;
  let chemicalBlob: ChemicalStructureBlob;
  if (saveFormat === MarvinChemicalFormat.CDX) {
    const dataURI = `data:binary/octet-stream;base64,${structure}`;
    chemicalBlob = dataURIToBlob(dataURI, filename);
  } else {
    chemicalBlob = textToBlob(structure);
    chemicalBlob.name = filename;
  }
  return chemicalBlob;
}

/**
 * Based on initiateTextDownload in client/util/TextToBlobUtil.ts,
 * modified to support binary (CDX) files
 */
export function downloadChemicalStructure(
  structure: string,
  filename: string,
  format: MarvinChemicalFormat
): void {
  if (format === MarvinChemicalFormat.CDX) {
    const dataURI = `data:binary/octet-stream;base64,${structure}`;
    initiateFileDownload(filename, dataURI);
  } else {
    initiateTextDownload(structure, filename);
  }
}
